import { useEffect, useState } from 'react'
import { usePublicClient } from 'wagmi'
import { ADDRESSES, IS_LIVE } from '@/lib/contracts'
import poolAbi from '@/lib/abis/HorizonLendingPool.json'
import veAbi from '@/lib/abis/HorizonVoteEscrow.json'
import horizonAbi from '@/lib/abis/HorizonToken.json'
import { getLogsChunked } from '@/lib/getLogsChunked'
import { fetchGatewayWethActivity } from '@/lib/gatewayActivity'

function ev(abi, name) { return abi.find((x) => x.type === 'event' && x.name === name) }

const SUPPLIED = ev(poolAbi, 'Supplied')
const WITHDRAWN = ev(poolAbi, 'Withdrawn')
const BORROWED = ev(poolAbi, 'Borrowed')
const REPAID = ev(poolAbi, 'Repaid')
const LOCK_CREATED = ev(veAbi, 'LockCreated')
const LOCK_INCREASED = ev(veAbi, 'LockIncreased')
const LOCK_WITHDRAWN = ev(veAbi, 'Withdrawn')
const TRANSFER = ev(horizonAbi, 'Transfer')

/// This wallet's own money-market net position (supplied minus debt, both reserves), staked
/// HORIZON, and HORIZON wallet balance, all over time - reconstructed from
/// this wallet's own indexed events via `getLogsChunked` (see that helper and `useAllBorrowers`'s
/// own doc comment for why - windowed scans instead of one wide call, so a real mainnet
/// deployment's history doesn't exceed an RPC provider's `eth_getLogs` range cap). `getLogs`
/// filters server-side by the indexed `user` (or `from`/`to`, for HORIZON's own Transfer) topic, so
/// this is a handful of small scans per wallet, not the scan-everyone-then-filter
/// `useAllBorrowers` does for the Liquidations page.
///
/// HORIZON wallet balance is reconstructed from real ERC20 Transfer events (in minus out) - this is
/// exact, not an approximation, since a wallet's balance truly is the running sum of its own
/// transfers. The LP-farm leg went with the farm itself. ALM vault holdings are deliberately NOT
/// back-filled in its place: a vault share's historical USD value needs that vault's NAV at each
/// past block, which no event carries, and this app has no historical price reconstruction
/// anywhere. Leaving it out understates history rather than inventing it - Portfolio's live
/// headline figure counts vaults in full.
export function useUserPortfolioHistory(user) {
  const client = usePublicClient()
  const [series, setSeries] = useState(null) // null while loading, [] if nothing/failed
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!IS_LIVE || !client || !user) { setSeries(null); setLoading(false); return }
    let cancelled = false

    async function run() {
      setLoading(true)
      try {
        const fromBlock = ADDRESSES.LENDING_POOL_DEPLOY_BLOCK ? BigInt(ADDRESSES.LENDING_POOL_DEPLOY_BLOCK) : 0n
        // Lock events replace the retired staking's Staked/Unstaked (entry 182) - a lock's
        // create/increase legs are both inflows into the same "committed HORIZON" series.
        const ve = ADDRESSES.HORIZON_VOTE_ESCROW
        const [
          supplied, withdrawn, borrowed, repaid, locked, lockIncreased, lockWithdrawn,
          horizonIn, horizonOut,
        ] = await Promise.all([
          getLogsChunked(client, { address: ADDRESSES.LENDING_POOL, event: SUPPLIED, args: { user }, fromBlock }),
          getLogsChunked(client, { address: ADDRESSES.LENDING_POOL, event: WITHDRAWN, args: { user }, fromBlock }),
          getLogsChunked(client, { address: ADDRESSES.LENDING_POOL, event: BORROWED, args: { user }, fromBlock }),
          getLogsChunked(client, { address: ADDRESSES.LENDING_POOL, event: REPAID, args: { user }, fromBlock }),
          ve ? getLogsChunked(client, { address: ve, event: LOCK_CREATED, args: { user }, fromBlock }) : Promise.resolve([]),
          ve ? getLogsChunked(client, { address: ve, event: LOCK_INCREASED, args: { user }, fromBlock }) : Promise.resolve([]),
          ve ? getLogsChunked(client, { address: ve, event: LOCK_WITHDRAWN, args: { user }, fromBlock }) : Promise.resolve([]),
          getLogsChunked(client, { address: ADDRESSES.HORIZON_TOKEN, event: TRANSFER, args: { to: user }, fromBlock }),
          getLogsChunked(client, { address: ADDRESSES.HORIZON_TOKEN, event: TRANSFER, args: { from: user }, fromBlock }),
        ])

        const isEth = (l) => l.args.asset?.toLowerCase() === ADDRESSES.WETH.toLowerCase()

        const points = []
        for (const l of supplied) {
          const amt = Number(l.args.amount) / 1e18
          points.push({ block: l.blockNumber, idx: l.logIndex, ...(isEth(l) ? { suppliedEth: amt } : { suppliedUsdx: amt }) })
        }
        for (const l of withdrawn) {
          const amt = Number(l.args.amount) / 1e18
          points.push({ block: l.blockNumber, idx: l.logIndex, ...(isEth(l) ? { suppliedEth: -amt } : { suppliedUsdx: -amt }) })
        }
        for (const l of borrowed) {
          const amt = Number(l.args.amount) / 1e18
          points.push({ block: l.blockNumber, idx: l.logIndex, ...(isEth(l) ? { debtEth: amt } : { debtUsdx: amt }) })
        }
        for (const l of repaid) {
          const amt = Number(l.args.amount) / 1e18
          points.push({ block: l.blockNumber, idx: l.logIndex, ...(isEth(l) ? { debtEth: -amt } : { debtUsdx: -amt }) })
        }
        for (const l of locked) points.push({ block: l.blockNumber, idx: l.logIndex, stakedHorizonDelta: Number(l.args.amount) / 1e18 })
        for (const l of lockIncreased) points.push({ block: l.blockNumber, idx: l.logIndex, stakedHorizonDelta: Number(l.args.addedAmount) / 1e18 })
        for (const l of lockWithdrawn) points.push({ block: l.blockNumber, idx: l.logIndex, stakedHorizonDelta: -Number(l.args.amount) / 1e18 })
        for (const l of horizonIn) points.push({ block: l.blockNumber, idx: l.logIndex, horizonBalanceDelta: Number(l.args.value) / 1e18 })
        for (const l of horizonOut) points.push({ block: l.blockNumber, idx: l.logIndex, horizonBalanceDelta: -Number(l.args.value) / 1e18 })

        // ETH supplies/withdrawals go through WETHGateway, which credits the GATEWAY (not this wallet)
        // in Supplied/Withdrawn - so the scan above captured only this wallet's DEBT for the ETH leg,
        // which is exactly why the chart's net went negative. Reconstruct the WETH supply leg from the
        // receipt-token transfers (exact underlying ETH amount, +supply / -withdraw).
        const gwWeth = await fetchGatewayWethActivity(client, user, fromBlock)
        for (const r of gwWeth) {
          points.push({ block: r.blockNumber, idx: r.logIndex, suppliedEth: r.sign * (Number(r.amount) / 1e18) })
        }

        if (points.length === 0) { if (!cancelled) setSeries([]); return }

        points.sort((a, b) => (a.block === b.block ? a.idx - b.idx : Number(a.block - b.block)))

        // One getBlock per unique block, not per event, same as useHistoricalStats.
        const uniqueBlocks = [...new Set(points.map((p) => p.block))]
        const blockTimes = new Map()
        await Promise.all(uniqueBlocks.map(async (bn) => {
          const block = await client.getBlock({ blockNumber: bn })
          blockTimes.set(bn, Number(block.timestamp) * 1000)
        }))

        let suppliedUsdx = 0, suppliedEth = 0, debtUsdx = 0, debtEth = 0, stakedHorizon = 0, horizonBalance = 0
        const out = []
        for (const p of points) {
          suppliedUsdx += p.suppliedUsdx ?? 0
          suppliedEth += p.suppliedEth ?? 0
          debtUsdx += p.debtUsdx ?? 0
          debtEth += p.debtEth ?? 0
          stakedHorizon += p.stakedHorizonDelta ?? 0
          horizonBalance += p.horizonBalanceDelta ?? 0
          out.push({
            t: blockTimes.get(p.block),
            suppliedUsdx, suppliedEth, debtUsdx, debtEth, stakedHorizon, horizonBalance,
          })
        }

        if (!cancelled) setSeries(out)
      } catch (e) {
        console.error('useUserPortfolioHistory: getLogs scan failed', e)
        if (!cancelled) setSeries([])
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    run()
    return () => { cancelled = true }
  }, [client, user])

  return { series, loading }
}
